/***************************************************************************************
 * FileName      : QuizService.ts
 * Description   : 장비별 객관식 평가 문항 데이터 및 채점 서비스
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.12
 * Modified Date :
 * Reference     : doc/equipment-description/01-1_heat_exchanger_test.md,
 *                 doc/equipment-description/02-1_distillation_test.md,
 *                 doc/equipment-description/03-1_circulation_test.md
 ***************************************************************************************/

// 2026.02.12 : 박은정 : 퀴즈 문항 타입 정의
export interface QuizQuestion {
  id: number;
  category: string;
  question: string;
  choices: string[];
  answer: number; // 0-based index
  explanation: string;
}

// 2026.02.12 : 박은정 : 채점 결과 타입 정의
export interface QuizResult {
  total: number;
  correct: number;
  score: number; // 100점 만점
  passed: boolean; // 70점 이상 합격
  details: { questionId: number; selected: number; correct: number; isCorrect: boolean }[];
}

// 2026.02.12 : 박은정 : 장비별 객관식 평가 문항 데이터 (doc/equipment-description/*_test.md 기준)
const QUIZ_DATA: Record<string, QuizQuestion[]> = {
  // ─── 열교환기 15문항 (01-1_heat_exchanger_test.md) ───
  'heat-exchanger': [
    {
      id: 1,
      category: '열교환기 구성',
      question: '본 열교환기 실험장치에 구성된 열교환기의 종류로 올바른 것은?',
      choices: [
        '판형 열교환기, 쉘앤튜브 열교환기 (2종)',
        '판형 열교환기, 이중관 열교환기, 공냉식 열교환기 (3종)',
        '판형 열교환기, 쉘앤튜브 열교환기, 침지식 코일 열교환기 (3종)',
        '쉘앤튜브 열교환기, 핀튜브 열교환기 (2종)',
      ],
      answer: 2,
      explanation: '본 장치는 판형 열교환기(PH-103), 쉘앤튜브 열교환기(HE-104), 그리고 냉동기 증발기 코일 형태의 침지식 코일 열교환기 총 3종으로 구성되어 있습니다.',
    },
    {
      id: 2,
      category: '히트펌프 역할',
      question: '히트펌프(CH-107)의 역할로 올바른 설명은?',
      choices: [
        '냉수만 생산하고, 온수는 전기히터로만 가열한다',
        '증발기로 냉수를 냉각하고, 응축기의 응축열로 온수를 가열한다',
        '온수만 생산하고, 냉수는 외부 냉각수로 냉각한다',
        '냉매를 직접 열교환기로 보내 열교환한다',
      ],
      answer: 1,
      explanation: 'CH-107은 수냉식 히트펌프로, 증발기 코일(TK-101 내 침지)로 냉수를 냉각하고, 응축기의 응축열로 온수조(TK-105)를 가열하는 이중 역할을 수행합니다.',
    },
    {
      id: 3,
      category: '인터록 조건',
      question: 'CH-107(히트펌프)을 기동하기 위한 선행 조건은?',
      choices: [
        'PP-102(마그네트 펌프)가 운전 중이어야 한다',
        'EH-108(전기히터)가 OFF 상태이어야 한다',
        'TCV-101이 100% OPEN 상태이어야 한다',
        'PP-106(순환 펌프)이 운전 중이어야 한다',
      ],
      answer: 3,
      explanation: 'CH-107은 응축기 냉각을 위해 온수 순환이 필요하므로, PP-106(순환 펌프)이 반드시 운전 중이어야 기동할 수 있습니다. 이는 Interlock 조건입니다.',
    },
    {
      id: 4,
      category: '판형 열교환기 특성',
      question: '판형 열교환기(PH-103)의 특징으로 올바르지 않은 것은?',
      choices: [
        '고온·고압 환경에서 쉘앤튜브보다 더 적합하다',
        '동일 용량 대비 설치 면적이 작다',
        '높은 열전달 계수를 가진다',
        'Counter-Current Flow로 열교환 효율이 높다',
      ],
      answer: 0,
      explanation: '판형 열교환기는 콤팩트하고 열전달 효율이 높지만, 고온·고압 환경에서는 쉘앤튜브 열교환기가 더 적합합니다. 판형은 Gasket 내압 한계로 인해 고압 적용에 제한이 있습니다.',
    },
    {
      id: 5,
      category: '쉘앤튜브 구조',
      question: '본 장치의 쉘앤튜브 열교환기(HE-104)에서 온수가 흐르는 부분은?',
      choices: [
        'Shell Side (7 Pass)',
        'Tube Side (7 Pass)',
        'Tube Side (3 Pass)',
        'Shell Side (3 Pass)',
      ],
      answer: 2,
      explanation: 'HE-104는 Tube Side에 온수가 3 Pass로 흐르고, Shell Side에 냉수가 7 Pass로 흐르는 구조입니다. 냉수는 하부 유입→상부 토출로 Air Pocket 방지 설계입니다.',
    },
    {
      id: 6,
      category: '열전달량 공식',
      question: '열전달량(Q)을 계산하는 공식으로 올바른 것은?',
      choices: [
        'Q = m × ΔT',
        'Q = ṁ × Cp × ΔT',
        'Q = U × ΔT',
        'Q = A × Cp × ΔT',
      ],
      answer: 1,
      explanation: '열전달량 Q = ṁ(질량유량) × Cp(비열) × ΔT(온도차)입니다. 질량유량은 단위시간당 흐르는 유체의 질량(kg/s)입니다.',
    },
    {
      id: 7,
      category: 'PP-102 자동운전',
      question: 'PP-102(마그네트 펌프)의 자동운전 시 제어 방식은?',
      choices: [
        'ON/OFF 제어로만 운전한다',
        'SV(Hz)를 입력하면 해당 속도로 고정 운전한다',
        '온도 SV를 입력하면 온도에 맞춰 속도를 제어한다',
        'MV(유량값)를 입력하면 인버터가 자동으로 속도를 제어한다',
      ],
      answer: 3,
      explanation: '자동운전 시 원하는 유량값 MV(LPM)를 입력하면, 설정된 유량을 맞추기 위해 인버터가 자동으로 속도(Hz)를 제어합니다. 수동운전에서만 SV(Hz) 직접 입력이 유효합니다.',
    },
    {
      id: 8,
      category: 'TCV-101 기능',
      question: 'TCV-101(3WAY 밸브)의 주요 기능은?',
      choices: [
        '온수의 BY-PASS 유량을 조절하여 열교환기 출구온도를 제어한다',
        '냉수의 유량을 ON/OFF 방식으로 제어한다',
        '냉매의 흐름 방향을 전환한다',
        '탱크의 수위를 조절한다',
      ],
      answer: 0,
      explanation: 'TCV-101은 3WAY 밸브로, BY-PASS 온수유량의 비율을 조절(개도 0~100%)하여 열교환기 출구온도 변화를 제어합니다. 자동운전 시 최소 50% OPEN을 유지합니다.',
    },
    {
      id: 9,
      category: '냉수조 투시창',
      question: '냉수조(TK-101)에 설치된 투시창의 목적은?',
      choices: [
        '탱크 내부 수위를 육안으로 확인하기 위함',
        '탱크 내부 온도를 측정하기 위함',
        '냉동기 증발기 코일을 외부에서 관찰하기 위함',
        '냉수의 색상 변화를 확인하기 위함',
      ],
      answer: 2,
      explanation: '냉수조(TK-101) 내부에는 냉동기 증발기 코일이 침지되어 있으며, 투시창(Φ200)을 통해 증발기 코일의 동작 상태를 외부에서 육안으로 관찰할 수 있습니다.',
    },
    {
      id: 10,
      category: 'EH-108 제어방식',
      question: 'EH-108(전기히터)의 제어 방식으로 올바른 것은?',
      choices: [
        '설정 온도에 도달하면 출력을 비례 제어한다',
        'MIN 온도 이하에서 ON, MAX 온도 이상에서 OFF 된다',
        '항상 일정한 출력으로 가열한다',
        '유량에 따라 출력을 자동 조절한다',
      ],
      answer: 1,
      explanation: 'EH-108은 MIN/MAX 온도 설정에 따른 ON/OFF 자동 제어 방식입니다. MIN 설정온도 이하에서 자동 ON, MAX 설정온도 이상에서 자동 OFF됩니다.',
    },
    {
      id: 11,
      category: 'LMTD 용도',
      question: '대수평균온도차(LMTD)에 대한 설명으로 올바른 것은?',
      choices: [
        '입구와 출구 온도차의 산술평균이다',
        '고온 유체와 저온 유체의 온도 합이다',
        '열전달 계수를 계산하는 데 사용된다',
        '열교환기 설계 시 필요 전열면적을 산정하는 데 사용된다',
      ],
      answer: 3,
      explanation: 'LMTD(Log Mean Temperature Difference)는 열교환기 입출구 온도차를 대수평균으로 계산한 값으로, Q = U × A × LMTD 공식에서 필요 전열면적(A) 산정에 핵심적으로 사용됩니다.',
    },
    {
      id: 12,
      category: '온수조 열원',
      question: '본 장치의 온수조(TK-105)를 가열하는 열원으로 올바른 것은?',
      choices: [
        '냉동기 응축열 + 전기히터(EH-108)',
        '전기히터(EH-108) 단독',
        '스팀 가열',
        '냉동기 증발열',
      ],
      answer: 0,
      explanation: '온수조(TK-105)는 냉동기(CH-107) 응축기의 응축열과 전기히터(EH-108) 두 가지 열원으로 가열됩니다. 응축열 활용으로 에너지 효율을 높이고, 전기히터로 온도 미세 조정이 가능합니다.',
    },
    {
      id: 13,
      category: 'HMI 기능',
      question: "HMI 화면에서 'TAG 보기' 버튼을 1초 이상 누르면 어떤 변화가 일어나는가?",
      choices: [
        '자동운전 모드로 전환된다',
        '알람 이력 화면으로 이동한다',
        'TAG 번호가 기기 명칭으로 변경된다',
        'P&ID 화면에서 TABLE 화면으로 전환된다',
      ],
      answer: 2,
      explanation: 'TAG 보기 버튼을 1초 이상 누르면 TAG 번호(예: PP-102)가 기기 명칭(예: MAGNETIC PUMP)으로 변경되어 표시됩니다. 이는 학습자의 기기 식별을 돕기 위한 기능입니다.',
    },
    {
      id: 14,
      category: '열수지',
      question: '열수지(Heat Balance)의 정상상태 조건으로 올바른 것은?',
      choices: [
        'Q_hot > Q_cold',
        'Q_hot = Q_cold',
        'Q_hot < Q_cold',
        'Q_hot + Q_cold = 0',
      ],
      answer: 1,
      explanation: '정상상태에서 에너지 보존 법칙에 의해 고온측이 방출하는 열량(Q_hot)과 저온측이 흡수하는 열량(Q_cold)은 같아야 합니다. 즉, Q_hot = Q_cold 입니다.',
    },
    {
      id: 15,
      category: '산업 적용',
      question: '산업현장에서 쉘앤튜브 열교환기가 판형 열교환기보다 선호되는 조건은?',
      choices: [
        '설치 공간이 제한적인 경우',
        '청소가 자주 필요한 경우',
        '열교환 면적을 자주 변경해야 하는 경우',
        '고온·고압 환경에서 운전해야 하는 경우',
      ],
      answer: 3,
      explanation: '쉘앤튜브 열교환기는 고온·고압 환경에 적합하며, 석유화학 플랜트에서 약 85% 이상이 이 형태로 제작됩니다. 판형은 콤팩트하고 청소가 용이하지만, 내압 한계가 있어 고압 적용에 제한이 있습니다.',
    },
  ],

  // ─── 증류기 15문항 (02-1_distillation_test.md) ───
  distillation: [
    {
      id: 1,
      category: '증류 기본 원리',
      question: '증류(Distillation)의 기본 원리로 가장 적절한 것은?',
      choices: [
        '밀도 차이를 이용하여 혼합물을 분리한다',
        '용해도 차이를 이용하여 고체와 액체를 분리한다',
        '끓는점 차이를 이용하여 혼합물을 분리한다',
        '입자 크기 차이를 이용하여 혼합물을 분리한다',
      ],
      answer: 2,
      explanation: '증류는 혼합물 내 성분들의 끓는점(비등점) 차이를 이용하여 가열 → 증발 → 응축 과정을 통해 물질을 분리하는 단위조작입니다.',
    },
    {
      id: 2,
      category: '작동유체',
      question: '본 증류 실험장치에서 사용하는 작동유체의 구성으로 올바른 것은?',
      choices: [
        '메탄올 + 증류수 (메탄올 순도 80%)',
        '에탄올 + 증류수 (에탄올 순도 약 60%)',
        '아세톤 + 증류수 (아세톤 순도 70%)',
        '에탄올 + 글리세롤 (에탄올 순도 50%)',
      ],
      answer: 1,
      explanation: '본 장치는 증발온도 78.3℃인 에탄올과 증발온도 100℃인 증류수를 혼합하여 에탄올 순도 60% 내외로 충진합니다.',
    },
    {
      id: 3,
      category: '비등점',
      question: '에탄올과 물의 비등점 차이는 약 몇 ℃인가?',
      choices: [
        '약 10℃',
        '약 22℃',
        '약 35℃',
        '약 50℃',
      ],
      answer: 1,
      explanation: '에탄올의 비등점은 78.3℃, 물의 비등점은 100℃로, 그 차이는 약 21.7℃(≒22℃)입니다.',
    },
    {
      id: 4,
      category: '증류탑 구조',
      question: '본 실험장치 증류탑(DT-203)의 구조로 올바른 것은?',
      choices: [
        '2단, 각 단 BUBBLE CAP 3개',
        '3단, 각 단 BUBBLE CAP 4개',
        '4단, 각 단 BUBBLE CAP 5개 + DOWNCOMER 1개',
        '5단, 각 단 BUBBLE CAP 6개',
      ],
      answer: 2,
      explanation: '증류탑(DT-203)은 4단으로 구성되며, 각 단은 5개의 BUBBLE CAP과 1개의 DOWNCOMER로 이루어져 있습니다.',
    },
    {
      id: 5,
      category: '기기 매칭',
      question: '재비기(Reboiler)의 역할을 수행하는 본 실험장치의 기기 TAG는?',
      choices: [
        'TK-201',
        'EH-202',
        'HE-204',
        'KO-205',
      ],
      answer: 1,
      explanation: 'EH-202(전기히터, 5kW)가 재비기 역할을 수행하여 보일러 탱크 내 혼합용액을 80~90℃로 가열·기화시킵니다.',
    },
    {
      id: 6,
      category: '자동운전 조건 - 전기히터',
      question: 'EH-202(전기히터)의 자동운전 조건으로 올바른 것은?',
      choices: [
        'MIN 70℃ 이하 ON, MAX 80℃ 이상 OFF',
        'MIN 80℃ 이하 ON, MAX 90℃ 이상 OFF',
        'MIN 85℃ 이하 ON, MAX 95℃ 이상 OFF',
        'MIN 90℃ 이하 ON, MAX 100℃ 이상 OFF',
      ],
      answer: 1,
      explanation: 'EH-202는 On-Off 제어 방식으로, 설정온도 MIN(80℃) 이하에서 자동 ON, MAX(90℃) 이상에서 자동 OFF됩니다.',
    },
    {
      id: 7,
      category: '자동운전 조건 - 리턴밸브',
      question: 'MOV-201(리턴밸브)의 자동운전 조건에서 OPEN되는 수위 기준은?',
      choices: [
        'LIT-202 ≥ 100mm',
        'LIT-202 ≥ 150mm',
        'LIT-202 ≥ 200mm',
        'LIT-202 ≥ 250mm',
      ],
      answer: 2,
      explanation: 'MOV-201은 LIT-202(녹아웃드럼 수위) 기준으로 MAX 설정값(200mm) 이상 도달 시 OPEN, MIN 설정값(5mm) 이하 시 CLOSE합니다.',
    },
    {
      id: 8,
      category: '냉각수',
      question: '본 장치의 냉각수에 부동액(에틸렌글리콜)을 혼합하는 이유와 혼합 비율로 올바른 것은?',
      choices: [
        '열전달 효율 향상, 20wt.%',
        '동결 방지, 30wt.%',
        '부식 방지, 40wt.%',
        '점도 조절, 50wt.%',
      ],
      answer: 1,
      explanation: '냉각수라인에는 에틸렌글리콜 30wt.% 이상을 물과 혼합하여 영하 15℃까지 동결을 방지합니다.',
    },
    {
      id: 9,
      category: '응축기',
      question: 'HE-204(응축기)의 열교환 방식으로 올바른 것은?',
      choices: [
        '병류형 (Parallel Flow)',
        '대향류형 (Counter-current Flow)',
        '직교류형 (Cross Flow)',
        '혼합류형 (Mixed Flow)',
      ],
      answer: 1,
      explanation: 'HE-204는 TUBE SIDE(고온 에탄올)와 SHELL SIDE(냉각수)가 반대 방향으로 흐르는 대향류형으로 열전달 효율을 높였습니다.',
    },
    {
      id: 10,
      category: '계측기기',
      question: '녹아웃드럼(KO-205)의 수위를 측정하는 계측기 TAG는?',
      choices: [
        'LIT-201',
        'LIT-202',
        'TIT-203',
        'PIT-201',
      ],
      answer: 1,
      explanation: 'LIT-201은 보일러 탱크(TK-201) 수위, LIT-202는 녹아웃드럼(KO-205) 수위를 측정합니다.',
    },
    {
      id: 11,
      category: '안전 주의사항',
      question: '본 장치 운전 시 안전 주의사항으로 적절하지 않은 것은?',
      choices: [
        '증류탑은 85~90℃로 운전되므로 운전 중 접촉 금지',
        '고농도 에탄올 취급 시 반드시 환기 실시',
        '에탄올은 인화성 액체이므로 열, 스파크, 화염 주의',
        '에탄올 농도 60%는 완전 안전하므로 별도 보호구 불필요',
      ],
      answer: 3,
      explanation: '작동유체는 에탄올 농도 50~60%로 비교적 안전하지만, 분별증류된 고농도 에탄올 취급 시 눈 자극, 졸음, 현기증을 유발할 수 있으므로 주의가 필요합니다.',
    },
    {
      id: 12,
      category: '제어 용어',
      question: 'PV와 SV의 의미로 올바른 것은?',
      choices: [
        'PV: 설정값, SV: 측정값',
        'PV: 현재 측정값, SV: 목표 설정값',
        'PV: 최대값, SV: 최소값',
        'PV: 평균값, SV: 편차값',
      ],
      answer: 1,
      explanation: 'PV(Process Value)는 센서가 측정한 현재값, SV(Set Value)는 운전자가 설정한 목표값입니다.',
    },
    {
      id: 13,
      category: '히스테리시스',
      question: 'EH-202의 On-Off 제어에서 히스테리시스(Dead Band)는 몇 ℃인가?',
      choices: [
        '5℃',
        '10℃',
        '15℃',
        '20℃',
      ],
      answer: 1,
      explanation: 'EH-202는 MIN 80℃에서 ON, MAX 90℃에서 OFF되므로 히스테리시스(상하한 차이)는 10℃입니다.',
    },
    {
      id: 14,
      category: 'MOV-202',
      question: 'MOV-202(바이패스밸브)에 대한 설명으로 올바른 것은?',
      choices: [
        '자동운전 시 수위에 따라 자동 개폐된다',
        '자동운전이 없으며, 수동으로만 OPEN/CLOSE 가능하다',
        '자동운전 시 온도에 따라 자동 개폐된다',
        '자동운전 시 압력에 따라 자동 개폐된다',
      ],
      answer: 1,
      explanation: 'MOV-202는 별도의 자동운전이 없으며, 증류탑을 거치지 않고 단순 증발·응축 테스트 시 수동으로 조작합니다.',
    },
    {
      id: 15,
      category: '산업 적용',
      question: '다음 중 증류 공정이 적용되는 산업 분야가 아닌 것은?',
      choices: [
        '석유정제 - 원유를 LPG, 휘발유, 경유 등으로 분리',
        '제약 - 의약품 원료(API) 정제 및 용매 회수',
        '주류산업 - 증류주(소주, 위스키) 제조',
        '반도체 - 웨이퍼 식각(Etching) 공정',
      ],
      answer: 3,
      explanation: '반도체 웨이퍼 식각은 화학적 또는 물리적 방법으로 표면을 제거하는 공정으로, 증류와는 다른 원리입니다. 다만 반도체 분야에서도 초순수 제조에는 증류가 활용됩니다.',
    },
  ],

  // ─── 순환기 15문항 (03-1_circulation_test.md) ───
  circulation: [
    {
      id: 1,
      category: '교반 방식',
      question: '본 순환 공정 실험장치에서 사용하는 교반 방식으로 올바른 것은?',
      choices: [
        '임펠러 회전 교반 방식',
        'EDUCTOR & BUBBLE 교반 방식',
        '자석 교반기(Magnetic Stirrer) 방식',
        '초음파 교반 방식',
      ],
      answer: 1,
      explanation: '본 장치는 교육의 효과를 위해 통상적인 교반기 방식이 아닌 EDUCTOR & BUBBLE 교반 방식으로 다양하게 구성되어 있습니다.',
    },
    {
      id: 2,
      category: '1계열 원리',
      question: '1계열(TK-301)에서 외부 공기를 흡입하는 원리로 올바른 것은?',
      choices: [
        '블로워의 강제 송풍',
        'EDUCTOR 노즐 분사에 따른 부압 발생',
        '진공 펌프의 흡입력',
        '탱크 내부 압력 차이',
      ],
      answer: 1,
      explanation: '1계열은 펌프의 순환과 물의 EDUCTOR 노즐 분사에 따른 부압 발생으로 외부 공기를 흡입하여 용매와 혼합/순환/교반합니다.',
    },
    {
      id: 3,
      category: 'Venturi 효과',
      question: 'Venturi 효과에 대한 설명으로 올바른 것은?',
      choices: [
        '직경이 작은 관에서 큰 관으로 가면 유속이 빨라진다',
        '직경이 큰 관에서 작은 관으로 가면 유속이 빨라지고 압력이 낮아진다',
        '관의 직경과 관계없이 유속은 일정하다',
        '직경이 작은 관에서 압력이 높아진다',
      ],
      answer: 1,
      explanation: 'Venturi 효과는 베르누이 법칙의 일종으로, 직경이 큰 관에서 작은 관으로 유체가 들어가면 유속은 빨라지고 압력은 낮아지는 현상입니다.',
    },
    {
      id: 4,
      category: '용존산소',
      question: '맑은 물에 용해된 산소의 통상적인 농도와 본 장치 동작 후 목표 농도로 올바른 것은?',
      choices: [
        '통상 1~3ppm → 동작 후 5ppm 이상',
        '통상 6~9ppm → 동작 후 9ppm 이상',
        '통상 10~15ppm → 동작 후 20ppm 이상',
        '통상 15~20ppm → 동작 후 25ppm 이상',
      ],
      answer: 1,
      explanation: '맑은 물에는 산소가 통상 6~9ppm 정도 용해되어 있으며, 본 장치의 동작 후에는 9ppm 이상으로 높여주는 것을 구현합니다.',
    },
    {
      id: 5,
      category: '기기 매칭 - 산기통',
      question: '2계열(TK-306)에서 미세기포를 발생시키는 장치의 TAG 번호는?',
      choices: [
        'ED-303',
        'ED-307',
        'AT-305',
        'PP-302',
      ],
      answer: 2,
      explanation: 'AT-305(산기통)은 2계열에서 블로워가 공급하는 공기를 받아 AIR LIFT 작용으로 미세기포를 발생시키는 장치입니다.',
    },
    {
      id: 6,
      category: '인버터 설정 범위',
      question: '분사펌프(PP-302)의 인버터 속도 설정 범위로 올바른 것은?',
      choices: [
        '0 ~ 30 Hz',
        '0 ~ 50 Hz',
        '0 ~ 60 Hz',
        '0 ~ 100 Hz',
      ],
      answer: 2,
      explanation: 'PP-302의 수동운전 시 인버터 SV는 0.00 ~ 60.00 Hz 범위에서 직접 입력하여 속도를 제어합니다.',
    },
    {
      id: 7,
      category: '자동운전 조건 - PP-302',
      question: '자동운전(AUTO START) 시 PP-302의 제어 기준으로 올바른 것은?',
      choices: [
        '인버터 속도 SV (Hz)',
        'FQIT-301 유량 SV (m³/h)',
        'PIT-301 압력 SV (bar)',
        'OIT-301 농도 SV (mg/L)',
      ],
      answer: 1,
      explanation: 'PP-302 자동운전 시 FQIT-301 유량값 SV(m³/h)를 입력하면 설정된 유량에 따라 인버터가 자동 제어됩니다. 이때 인버터 속도 Hz SV는 무시됩니다.',
    },
    {
      id: 8,
      category: '자동운전 조건 - BL-304',
      question: '블로워(BL-304)의 자동운전 제어 기준으로 올바른 것은?',
      choices: [
        'FQIT-301 유량 SV (m³/h)',
        'OIT-302 농도 SV (mg/L)',
        'PIT-302 압력 SV (mmAq)',
        '인버터 속도 SV (Hz)',
      ],
      answer: 2,
      explanation: 'BL-304 자동운전 시 PIT-302 압력값 SV(mmAq)를 입력하면 설정된 압력에 따라 인버터가 자동 제어됩니다.',
    },
    {
      id: 9,
      category: '운전 모드',
      question: 'LOCAL 모드와 REMOTE 모드에 대한 설명으로 올바른 것은?',
      choices: [
        'LOCAL은 자동운전, REMOTE는 수동운전이다',
        'LOCAL은 수동운전, REMOTE는 자동운전이다',
        '두 모드 모두 자동운전이다',
        '두 모드 모두 수동운전이다',
      ],
      answer: 1,
      explanation: 'LOCAL(수동)은 운전자가 인버터 SV(Hz)를 직접 입력하여 속도를 제어하고, REMOTE(자동)는 시스템이 유량/압력 SV에 따라 인버터를 자동 제어합니다.',
    },
    {
      id: 10,
      category: 'EDUCTOR MIXER',
      question: 'EDUCTOR MIXER(ED-307)의 기능으로 올바른 것은?',
      choices: [
        '탱크 상부에서 공기를 흡입한다',
        '분사 운동에너지로 주변 용매 약 4배를 흡입하여 혼합/교반한다',
        '블로워의 공기를 여과한다',
        '탱크의 수위를 측정한다',
      ],
      answer: 1,
      explanation: 'EDUCTOR MIXER(ED-307)는 탱크 측면 하부에서 분사되는 용매의 운동에너지로 4배 정도의 주변 용매를 흡입하여 혼합/교반합니다.',
    },
    {
      id: 11,
      category: '제어 용어',
      question: 'PV와 SV의 의미로 올바른 것은?',
      choices: [
        'PV: 설정값, SV: 측정값',
        'PV: 현재 측정값, SV: 목표 설정값',
        'PV: 최대값, SV: 최소값',
        'PV: 평균값, SV: 편차값',
      ],
      answer: 1,
      explanation: 'PV(Present Value)는 센서가 측정한 현재값, SV(Set Value)는 운전자가 설정한 목표값입니다.',
    },
    {
      id: 12,
      category: 'AUTO START 조작',
      question: 'AUTO START / AUTO STOP 버튼의 올바른 조작 방법은?',
      choices: [
        '버튼을 가볍게 한 번 터치한다',
        '버튼을 1초 이상 눌러준다',
        '버튼을 두 번 연속 클릭한다',
        '버튼을 5초 이상 길게 누른다',
      ],
      answer: 1,
      explanation: 'AUTO STOP / AUTO START 버튼을 1초 동안 눌러주면 MODE가 변경됩니다.',
    },
    {
      id: 13,
      category: '산기통 원리',
      question: '산기통(AT-305)의 작동 원리로 올바른 것은?',
      choices: [
        '모터로 임펠러를 회전시켜 교반한다',
        'AIR LIFT 작용으로 공기와 물이 함께 상승하여 미세기포를 발생시킨다',
        '초음파 진동으로 기포를 생성한다',
        '열을 가하여 기포를 발생시킨다',
      ],
      answer: 1,
      explanation: '산기통은 공기배관에 연결되어 하부로 공기를 공급받으며, AIR LIFT 작용으로 공기와 주변 물이 함께 상승하여 미세기포를 발생시킵니다.',
    },
    {
      id: 14,
      category: '유지관리',
      question: 'BL-304 운전 시 주의사항으로 올바른 것은?',
      choices: [
        '항상 최대 유량으로 운전해야 한다',
        '과도한 유량으로 운전하면 탱크 상부로 물방울이 비산될 수 있으므로 적정 유량으로 운전한다',
        '탱크에 물이 없어도 운전할 수 있다',
        '운전 중 탱크 내부를 손으로 확인해야 한다',
      ],
      answer: 1,
      explanation: 'BL-304의 경우 과도한 유량으로 운전하면 탱크 상부로 물방울이 비산될 수 있으므로 적정 유량으로 운전하여야 합니다.',
    },
    {
      id: 15,
      category: '산업 적용',
      question: '다음 중 EDUCTOR 또는 산기통을 활용한 순환/교반 공정이 적용되는 산업 분야가 아닌 것은?',
      choices: [
        '수처리 - 폭기조 산기관 시스템',
        '양식업 - 양어장 용존산소 공급',
        '화학·제약 - 반응조 혼합/교반',
        '반도체 - 웨이퍼 노광(Lithography) 공정',
      ],
      answer: 3,
      explanation: '반도체 웨이퍼 노광은 빛을 이용하여 회로 패턴을 전사하는 공정으로, EDUCTOR/산기통 교반과는 다른 원리입니다. 다만 반도체 분야에서도 세정액 순환 등에는 유사 기술이 활용됩니다.',
    },
  ],
};

// 2026.02.12 : 박은정 : 퀴즈 서비스 클래스 (싱글턴 패턴)
export class QuizService {
  private static instance: QuizService;

  private constructor() {}

  static getInstance(): QuizService {
    if (!QuizService.instance) {
      QuizService.instance = new QuizService();
    }
    return QuizService.instance;
  }

  // 장비별 퀴즈 문항 조회
  getQuestions(equipmentId: string): QuizQuestion[] {
    return QUIZ_DATA[equipmentId] || [];
  }

  // 총 문항 수
  getQuestionCount(equipmentId: string): number {
    return (QUIZ_DATA[equipmentId] || []).length;
  }

  // 채점
  gradeQuiz(equipmentId: string, answers: Record<number, number>): QuizResult {
    const questions = this.getQuestions(equipmentId);
    const total = questions.length;
    let correct = 0;
    const details: QuizResult['details'] = [];

    for (const q of questions) {
      const selected = answers[q.id];
      const isCorrect = selected === q.answer;
      if (isCorrect) correct++;
      details.push({
        questionId: q.id,
        selected: selected ?? -1,
        correct: q.answer,
        isCorrect,
      });
    }

    const score = total > 0 ? Math.round((correct / total) * 100) : 0;

    return {
      total,
      correct,
      score,
      passed: score >= 70,
      details,
    };
  }
}

